import { useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
    Alert,
    Spinner,
    Col,
    Row,
} from 'reactstrap';
import { useQuery } from '@tanstack/react-query';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { httpClient } from '@s20/services/httpClient';
import { FlexEndRow, toasty } from '../../common/forms/FormElements';
import SlideForm from '../../common/forms/SlideForm';
import { SaveButton } from '../../common/forms/SaveButton';
import { EquipmentPickupForm } from '../../common/EquipmentPickupForm';
import { submitPickupTimesheet } from '../../common/submitPickupTimesheet';
import { ApiRoutes } from '../../Common';
import { Section } from '../QuickJob/Section';
import { DispatchLocation } from './DispatchLocation';
import { Customer } from './Customer';
import { Contract } from './Contract';
import { Project } from './Project';

const initialValues = {
    dispatchLocationId: null,
    companyId: null,
    contractId: null,
    projectId: null,
};

export const QuickReturn = ({ show, onClose }) => {
    const [selectedProjectId, setSelectedProjectId] = useState(null);
    const [pickupDetails, setPickupDetails] = useState(null);

    const {
        data: project,
        isLoading: projectLoading,
        error: projectError,
    } = useQuery({
        queryKey: ['quickReturn', 'project', selectedProjectId],
        queryFn: () => httpClient.js(ApiRoutes.project.byId(selectedProjectId)),
        enabled: !!selectedProjectId,
        select: (resp) => resp?.project ?? null,
    });

    const handleProjectChanged = useCallback((projId) => {
        setSelectedProjectId(projId);
        setPickupDetails(null);
    }, []);

    const handleClose = useCallback(() => {
        setSelectedProjectId(null);
        setPickupDetails(null);
        onClose();
    }, [onClose]);

    const handleSubmit = useCallback(async () => {
        if (!pickupDetails) return;
        try {
            const tn = await submitPickupTimesheet(pickupDetails);
            toasty.success(`Timesheet ${tn} created successfully!`);
            handleClose();
        } catch (error) {
            if (!error?.message?.includes('pickup quantity greater than zero')) {
                toasty.error(error?.message || 'An error occurred while creating the timesheet.');
            }
        }
    }, [pickupDetails, handleClose]);

    return createPortal(
        <SlideForm
            size="col-xl-12 col-md-12 col-xs-12"
            id="quickReturnForm"
            name="quickReturnForm"
            show={show}
            onClose={handleClose}
            fullScreen
            hideToolbar
            minimize
            className="no-padding"
            formTitle="Quick Return"
            initialValues={initialValues}
            onSubmit={handleSubmit}
        >
            {({ values }) => (
                <div className="full-page-panel">
                    <Section>
                        <Row className="mt-3">
                            <Col
                                xs={12}
                                md={6}
                            >
                                <DispatchLocation />
                            </Col>
                            <Col
                                xs={12}
                                md={6}
                            >
                                <Customer />
                            </Col>
                        </Row>
                        <Row>
                            <Col
                                xs={12}
                                md={6}
                            >
                                <Contract />
                            </Col>
                            <Col
                                xs={12}
                                md={6}
                            >
                                <Project onProjectChanged={handleProjectChanged} />
                            </Col>
                        </Row>
                    </Section>

                    {selectedProjectId && projectLoading && (
                        <div className="d-flex justify-content-center my-3">
                            <Spinner />
                        </div>
                    )}

                    {projectError && (
                        <Alert
                            color="danger"
                            className="mx-3 mt-2"
                        >
                            {projectError.message || 'Failed to load project.'}
                        </Alert>
                    )}

                    {project && (
                        <Section color="white">
                            <EquipmentPickupForm
                                project={project}
                                contractId={values.contractId}
                                pickupDetails={pickupDetails}
                                setPickupDetails={setPickupDetails}
                            />
                            {pickupDetails && (
                                <FlexEndRow>
                                    <SaveButton
                                        label="Add"
                                        savingLabel="Adding"
                                        icon={faPlus}
                                        className="mt-3"
                                    />
                                </FlexEndRow>
                            )}
                        </Section>
                    )}
                </div>
            )}
        </SlideForm>,
        document.body,
    );
};
